// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { ChevronDown, ChevronLeft, ChevronRight, Filter, Inbox } from "lucide-react"
import {
  type EventFeedMode,
  useCycleEvents,
  useEventById,
} from "@/queries/queries.eventJournal"
import { useUrlPageParam } from "@/hooks/useUrlPageParam"
import { formatDate } from "@/utils"

const dotClassFor = (eventName: string): string => {
  const name = eventName.toLowerCase()
  if (name.includes("fail") || name.includes("error")) return "bg-failed"
  if (name.includes("complet") || name.includes("evaluated")) return "bg-primary"
  if (name.includes("snapshot") || name.includes("iteration"))
    return "bg-running"
  if (name.includes("mutat") || name.includes("cross")) return "bg-lineage"
  return "bg-text-tertiary"
}

// Events shown per page (matches the Backend default `limit=50`).
const PAGE_SIZE = 50

// PRD §US §11–14 §18 (slice 08): relevance toggle + server-side pagination.
// `ev_page` is URL-reflected (ADR-0006), namespaced to avoid colliding with
// the leaderboard's `lb_page` on the same dashboard route.
// INV: failure/error events are always present in the default view —
// guaranteed Backend-side by `*Failed`/`*Exhausted` rules; no client re-filter.
// INV-5: per-row payloads are metadata-only on the list; fetched on demand
// via `useEventById(eventId)` when a row expands.
export interface EventsPanelProps {
  cycleId?: string
}

export default function EventsPanel({ cycleId }: EventsPanelProps = {}) {
  const params = useParams<{ cycleId: string }>()
  const resolvedCycleId = cycleId ?? params.cycleId

  const [searchParams, setSearchParams] = useSearchParams()

  // Deep-linkable page state (ADR-0006): the events page lives in the
  // URL under `ev_page` (1-based), via the shared `useUrlPageParam` hook
  // (page 1 omitted, namespaced so it never collides with the
  // leaderboard's `lb_page` on the same dashboard route).
  const { page: requestedPage, setPage } = useUrlPageParam("ev_page")

  // Deep-linkable feed mode (ADR-0006 / ADR-0049): the relevant/all
  // toggle lives in the URL under `ev_mode`, mirroring `ev_page`. Only
  // the non-default `all` value is written; the canonical `relevant`
  // mode is kept out of the URL so shared/bookmarked links stay clean.
  const mode: EventFeedMode =
    searchParams.get("ev_mode") === "all" ? "all" : "relevant"
  const showAll = mode === "all"

  const requestedOffset = (requestedPage - 1) * PAGE_SIZE

  const eventsQuery = useCycleEvents({
    cycleId: resolvedCycleId,
    mode,
    offset: requestedOffset,
    limit: PAGE_SIZE,
  })
  const events = eventsQuery.data?.items ?? []
  const total = eventsQuery.data?.total ?? events.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Clamp a stale / hand-crafted out-of-range `?ev_page=N` (N beyond the
  // last real page) back to the last real page once `total` is known
  // (CORR-01). Without this, an overshooting offset returns an empty
  // page and the empty-state renders with no pagination controls — a
  // dead end even though the cycle has events. The clamped page both
  // drives the display and is written back so the URL stays honest.
  const page = Math.min(requestedPage, totalPages)
  const offset = (page - 1) * PAGE_SIZE
  useEffect(() => {
    if (eventsQuery.data && requestedPage > totalPages) {
      // History-replace so normalising a stale link doesn't add an entry.
      setPage(totalPages, { replace: true })
    }
  }, [eventsQuery.data, requestedPage, totalPages, setPage])

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  // Deep-linkable name filter (ADR-0006 / INV-7, issue
  // `cycle-health-panel` AC5): lives in the URL under `ev_filter`,
  // namespaced alongside `ev_page`/`ev_mode`, so a Cycle Health
  // top-error line can link straight into a pre-filtered events panel
  // and the resulting URL is shareable / back-button-restorable.
  const nameFilter = searchParams.get("ev_filter") ?? ""
  const setNameFilter = (next: string) => {
    const url = new URLSearchParams(searchParams)
    if (next) url.set("ev_filter", next)
    else url.delete("ev_filter")
    setSearchParams(url, { replace: false })
  }

  const goToPage = (next: number) => {
    setPage(Math.min(Math.max(next, 1), totalPages))
  }

  const setShowAll = (next: boolean) => {
    // Reflect the mode in the URL (ADR-0006): write `ev_mode=all` for
    // the firehose, drop the param for the default `relevant` mode so
    // canonical links stay clean (mirrors page-1 omission).
    const url = new URLSearchParams(searchParams)
    if (next) url.set("ev_mode", "all")
    else url.delete("ev_mode")
    // Switching mode renumbers the pages — reset to page 1.
    url.delete("ev_page")
    setSearchParams(url, { replace: false })
  }

  const toggleExpand = (eventId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(eventId)) {
        next.delete(eventId)
      } else {
        next.add(eventId)
      }
      return next
    })
  }

  const distinctNames = Array.from(new Set(events.map((e) => e.event_name)))
  const visibleEvents = nameFilter
    ? events.filter((e) => e.event_name === nameFilter)
    : events

  if (eventsQuery.isLoading) {
    return (
      <section className="box flex flex-col gap-3.5 p-5">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-tertiary">
          Events
        </span>
        <div
          role="region"
          aria-label="Events panel (loading)"
          className="text-center text-sm text-text-tertiary"
        >
          Loading panel…
        </div>
      </section>
    )
  }

  const filterDisabled = distinctNames.length === 0

  return (
    <section
      aria-label="Events panel"
      className="box flex flex-col gap-3.5 p-5"
    >
      <div className="flex flex-row flex-wrap items-center justify-between gap-3">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-text-tertiary">
          Events
        </span>
        <div className="flex flex-row flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 text-[12.5px] text-text-secondary">
            <input
              type="checkbox"
              aria-label="Show all events"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="size-3.5 cursor-pointer accent-primary"
            />
            Show all events
          </label>
          <div className="relative inline-flex items-center">
            <Filter className="pointer-events-none absolute left-3 size-[13px] text-text-tertiary" />
            <select
              aria-label="Filter by event name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              disabled={filterDisabled}
              className="h-[34px] appearance-none rounded-[10px] border border-border-strong bg-surface-strong pl-8 pr-8 text-[12.5px] text-text-secondary outline-none transition-colors hover:border-border-active disabled:opacity-50"
            >
              <option value="">All event names</option>
              {distinctNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 size-[13px] text-text-tertiary" />
          </div>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="box-dashed flex flex-col items-center justify-center gap-3 px-6 py-12">
          <span className="flex size-12 items-center justify-center rounded-[14px] border border-dashed border-border-active text-border-active">
            <Inbox className="size-6" />
          </span>
          <p className="text-sm font-medium text-text-secondary">
            No events recorded for this cycle.
          </p>
        </div>
      ) : (
        <>
          <ul
            aria-label="Events"
            className="flex flex-col overflow-hidden rounded-md border border-border"
          >
            {visibleEvents.map((event) => {
              const isExpanded = expandedIds.has(event.event_id)
              return (
                <li
                  key={event.event_id}
                  className="flex flex-col border-b border-border last:border-b-0"
                >
                  <button
                    type="button"
                    aria-label={`Expand event ${event.event_id}`}
                    aria-expanded={isExpanded}
                    onClick={() => toggleExpand(event.event_id)}
                    className="flex cursor-pointer flex-row items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-primary/[0.03]"
                  >
                    <span
                      className={`size-2 shrink-0 rounded-full ${dotClassFor(
                        event.event_name,
                      )}`}
                    />
                    <span className="flex-1 font-mono text-[13px] text-foreground">
                      {event.event_name}
                    </span>
                    <span className="font-mono text-[11.5px] text-text-tertiary">
                      {formatDate(event.occurred_at)}
                    </span>
                    <ChevronDown
                      className={`size-3.5 text-text-tertiary transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isExpanded ? (
                    <ExpandedPayload eventId={event.event_id} />
                  ) : null}
                </li>
              )
            })}
          </ul>
          {totalPages > 1 ? (
            <nav
              aria-label="Events pagination"
              className="flex flex-row items-center justify-between gap-3 pt-1"
            >
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                className="inline-flex items-center gap-1 rounded-[10px] border border-border-strong px-3 py-1.5 text-[12.5px] text-text-secondary transition-colors hover:border-border-active disabled:opacity-40"
              >
                <ChevronLeft className="size-[13px]" />
                Prev
              </button>
              <span className="font-mono text-[11.5px] text-text-tertiary">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
                className="inline-flex items-center gap-1 rounded-[10px] border border-border-strong px-3 py-1.5 text-[12.5px] text-text-secondary transition-colors hover:border-border-active disabled:opacity-40"
              >
                Next
                <ChevronRight className="size-[13px]" />
              </button>
            </nav>
          ) : null}
        </>
      )}
    </section>
  )
}

function ExpandedPayload({ eventId }: { eventId: string }) {
  const detailQuery = useEventById(eventId)

  if (detailQuery.isLoading) {
    return (
      <div
        role="region"
        aria-label={`Payload for ${eventId} (loading)`}
        className="border-t border-border bg-[#02100f] px-4 py-3 text-xs italic text-text-tertiary"
      >
        Loading payload…
      </div>
    )
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div
        role="region"
        aria-label={`Payload for ${eventId} (error)`}
        className="border-t border-border bg-[#02100f] px-4 py-3 text-xs italic text-failed-fg"
      >
        Failed to load payload.
      </div>
    )
  }

  const raw = detailQuery.data.payload_json
  let pretty: string = raw
  try {
    pretty = JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    pretty = raw
  }

  return (
    <div
      role="region"
      aria-label={`Payload for ${eventId}`}
      className="border-t border-border bg-[#02100f]"
    >
      <pre className="overflow-x-auto whitespace-pre px-4 py-3 font-mono text-xs leading-[1.7] text-text-secondary">
        {pretty}
      </pre>
    </div>
  )
}
